import { isSameOfferProductSet, offerProductSet, type OfferProductSetOption } from './offer-product-set';
import { isPropertyScopedProductSet, offerPropertyKey, type PropertyAddress } from './offer-property';

const REDIRECTABLE_OFFER_MAX_AGE_MS = 60 * 24 * 60 * 60 * 1000;

// Deep enough to see past the offers isRedirectableOffer skips: optionless ones (#30994) and
// untouched merge bundles (#35529), which are excluded here rather than filtered in Meilisearch.
export const REDIRECTABLE_OFFER_SCAN_LIMIT = 10;

export const REDIRECTABLE_OFFER_ATTRIBUTES: string[] = [
  'id',
  'parentId',
  'updatedDateTime',
  'options',
  'isMergeMinted'
];

type RedirectableOffer = {
  updatedDateTime?: string | null;
  options?: OfferProductSetOption[] | null;
  isMergeMinted?: boolean | null;
  /** The offer-history index stores the property address here (farm location first). */
  quote?: { submittedAddress?: PropertyAddress | null } | null;
};

/**
 * Whether `offer` is a newer revision of the quote being viewed: it has options, quotes the same
 * product set (#34790) and, for farm-system products, covers the same property (#36618). Callers that
 * send no `currentPropertyKey` (an older staff-fe bundle on the backend fallback) keep the
 * product-set-only rule.
 */
export function isRedirectableOffer(
  offer: RedirectableOffer | null | undefined,
  currentOptions: OfferProductSetOption[] | null | undefined,
  currentPropertyKey?: string | null
): boolean {
  if (!offer?.updatedDateTime || !Array.isArray(offer.options) || offer.options.length === 0) {
    return false;
  }
  if (offer.isMergeMinted) {
    return false;
  }
  const currentProductSet = offerProductSet(currentOptions);
  if (currentProductSet.length === 0) {
    return true;
  }
  if (!isSameOfferProductSet(offer.options, currentOptions)) {
    return false;
  }
  if (!currentPropertyKey || !isPropertyScopedProductSet(currentProductSet)) {
    return true;
  }
  return offerPropertyKey(offer.quote?.submittedAddress) === currentPropertyKey;
}

export function redirectableOfferCutoff(now: number = Date.now()): string {
  return new Date(now - REDIRECTABLE_OFFER_MAX_AGE_MS).toISOString();
}
